import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useSocket } from "../../contexts/SocketContext";
import {
  LoadingOverlay,
  TextInput,
  Text,
  Modal,
  Button,
  Group,
  Pagination,
  CloseButton,
} from "@mantine/core";
import { createTag, getTagsPage } from "../../services/tags";
import { createButtonStyles } from "../../services/globalColor.js";
import TagItem from "./TagItem";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import { usePermissions } from "../../contexts/PermissionsContext";

const Tags = () => {
  useSetPageTitle("Etiquetas");
  const { hasPermission } = usePermissions();
  const userData = useSelector((state) => state.user.user);
  const { socket } = useSocket();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [tagDataForEdit, setTagDataForEdit] = useState(null);

  const getTagsMethod = async ({ pageOverride } = {}) => {
    try {
      setLoading(true);
      if (userData?._id) {
        const targetPage = Number.isFinite(Number(pageOverride))
          ? Number(pageOverride)
          : currentPage;
        const page = await getTagsPage({
          page: targetPage,
          search: debouncedSearch,
        });
        setTags(Array.isArray(page?.items) ? page.items : []);
        if (Number.isFinite(Number(page?.limit))) {
          setPageLimit(Number(page.limit));
        }
        setTotalPages(
          Number.isFinite(Number(page?.totalPages))
            ? Number(page.totalPages)
            : 1
        );
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
      setTags([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?._id) {
      getTagsMethod();
    }
  }, [userData]);

  // Debounce search; immediate when cleared
  useEffect(() => {
    if (!searchText || searchText.trim().length === 0) {
      setDebouncedSearch("");
      return;
    }
    const id = setTimeout(() => setDebouncedSearch(searchText), 1000);
    return () => clearTimeout(id);
  }, [searchText]);

  // Reset to first page when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Fetch when page or search changes
  useEffect(() => {
    if (userData?._id) {
      getTagsMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, userData?._id]);

  useEffect(() => {
    if (!socket) return;
    const onAppUpsert = (payload) => {
      if (!(payload && payload.collection === "tags")) return;
      const result = payload?.result;
      const upserted = Array.isArray(result?.items) ? result.items : [];
      if (upserted.length === 0) {
        getTagsMethod({ pageOverride: currentPage });
        return;
      }
      setTags((prev) => {
        const prevIds = new Set(prev.map((t) => String(t._id)));
        let next = prev.map((t) => {
          const updated = upserted.find((u) => String(u._id) === String(t._id));
          return updated ? { ...t, ...updated } : t;
        });
        const capacity = Math.max(0, Number(pageLimit) - next.length);
        if (capacity > 0) {
          const newcomers = upserted.filter((u) => !prevIds.has(String(u._id)));
          if (newcomers.length > 0) next = next.concat(newcomers.slice(0, capacity));
        }
        return next;
      });
    };
    socket.on("app:upsert", onAppUpsert);
    return () => socket.off("app:upsert", onAppUpsert);
  }, [socket, currentPage, pageLimit]);

  // Client filter no longer needed; server handles search

  const closeTagManagerModalHandler = () => {
    setIsOpen(false);
    setTagDataForEdit(null);
  };

  const saveTagManagerModalHandler = async () => {
    await getTagsMethod({ pageOverride: currentPage });
    setTagDataForEdit(null);
  };

  if (!userData?._id) {
    return (
      <div className="tags container">
        <Text color="dimmed">No se encontró información del usuario</Text>
      </div>
    );
  }

  return (
    <>
      <LoadingOverlay
        visible={loading}
        overlayProps={{ blur: 2 }}
      />
      <Modal
        opened={isOpen}
        onClose={closeTagManagerModalHandler}
        title={tagDataForEdit ? "Editar etiqueta" : "Añadir etiqueta"}
      >
        <div>
          <TextInput
            label="Nombre"
            value={tagDataForEdit?.name || ""}
            onChange={(event) => {
              if (tagDataForEdit) {
                setTagDataForEdit({
                  ...tagDataForEdit,
                  name: event.currentTarget.value,
                });
              } else {
                setTagDataForEdit({ name: event.currentTarget.value });
              }
            }}
            required
            style={{ marginBottom: "1rem" }}
          />
        </div>
        <Group justify="flex-end">
          <Button
            onClick={async () => {
              if (tagDataForEdit) {
                await createTag({
                  name: tagDataForEdit.name,
                  userId: userData._id,
                });
              }
              saveTagManagerModalHandler();
              closeTagManagerModalHandler();
            }}
            loading={loading}
            variant="filled"
            
          >
            {tagDataForEdit ? "Guardar cambios" : "Añadir etiqueta"}
          </Button>
        </Group>
      </Modal>

      <div className="tags">
        <h1 className="hide-on-mobile">Etiquetas</h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <TextInput
            placeholder="Buscar etiquetas"
            value={searchText}
            onChange={(event) => setSearchText(event.currentTarget.value)}
            style={{ flex: 1, minWidth: 220 }}
            rightSection={
              searchText ? (
                <CloseButton
                  aria-label="Borrar búsqueda"
                  onClick={() => setSearchText("")}
                />
              ) : null
            }
            rightSectionPointerEvents="all"
          />
          {hasPermission({ feature: "etiquetas", action: "Crear" }) && (
            <Button
              type="button"
              onClick={() => setIsOpen(true)}
              style={{ minWidth: 120, fontWeight: 700 }}
            >
              Añadir
            </Button>
          )}
        </div>
        <div
          className="table-container"
          style={{ textAlign: "left" }}
        >
          {tags.length === 0 ? (
            <Text
              color="dimmed"
              align="center"
            >
              No hay etiquetas registradas
            </Text>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th >Nombre</th>
                  <th ></th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => (
                  <TagItem
                    key={tag?._id}
                    tag={tag}
                    getTagsMethod={getTagsMethod}
                  />
                ))}
              </tbody>
            </table>
          )}
          {tags.length > 0 && (
            <div
              style={{
                marginTop: 20,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Tags;
